import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { surveyApiService } from '../../api/surveyApi';
import {
    ArrowTopRightOnSquareIcon,
    ClockIcon,
    ChevronRightIcon,
    EyeIcon,
    CheckCircleIcon
} from '@heroicons/react/24/outline';
import { format, isWithinInterval, startOfDay, endOfDay } from 'date-fns';
import { tr } from 'date-fns/locale';
import DataTable from '../../components/ui/DataTable';
import TableAction, { TableActionGroup } from '../../components/ui/TableAction';
import DataFilter from '../../components/ui/DataFilter';

const SurveySubmissions = () => {
    const { slug } = useParams();
    const [survey, setSurvey] = useState(null);
    const [submissions, setSubmissions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({
        startDate: '',
        endDate: '',
        status: 'all'
    });
    const navigate = useNavigate();

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [surveyData, submissionResponse] = await Promise.all([
                    surveyApiService.getSurveyDetail(slug),
                    surveyApiService.getSubmissions(slug)
                ]);
                setSurvey(surveyData);
                const submissionData = Array.isArray(submissionResponse) ? submissionResponse : (submissionResponse?.results || []);
                setSubmissions(submissionData);
            } catch (error) {
                console.error('Error fetching submissions:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [slug]);

    const filterConfig = [
        {
            name: 'startDate',
            label: 'Başlangıç Tarihi',
            type: 'date',
        },
        {
            name: 'endDate',
            label: 'Bitiş Tarihi',
            type: 'date',
        },
        {
            name: 'status',
            label: 'Durum',
            type: 'select',
            options: [
                { value: 'completed', label: 'Tamamlandı' },
                { value: 'partial', label: 'Kısmi' }
            ],
            allLabel: 'Tüm Durumlar'
        }
    ];

    const filteredData = useMemo(() => {
        return submissions.filter(sub => {
            let matches = true;

            if (filters.startDate) {
                const start = startOfDay(new Date(filters.startDate));
                const date = new Date(sub.started_at);
                if (date < start) matches = false;
            }

            if (filters.endDate) {
                const end = endOfDay(new Date(filters.endDate));
                const date = new Date(sub.started_at);
                if (date > end) matches = false;
            }

            if (filters.status !== 'all') {
                if (filters.status === 'completed' && !sub.is_completed) matches = false;
                if (filters.status === 'partial' && sub.is_completed) matches = false;
            }

            return matches;
        });
    }, [submissions, filters]);

    const handleFilterChange = (name, value) => {
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const handleClearFilters = () => {
        setFilters({ startDate: '', endDate: '', status: 'all' });
    };

    const columns = [
        {
            header: 'ID / IP ADRESİ',
            accessorKey: 'id',
            size: 120,
            cell: ({ row }) => {
                const sub = row.original;
                return (
                    <div className="flex flex-col">
                        <span className="text-sm font-bold text-slate-900 dark:text-white leading-tight">#{sub.id}</span>
                        <span className="text-[10px] text-slate-400 font-mono tracking-tight">{sub.ip_address || 'Gizli'}</span>
                    </div>
                );
            }
        },
        {
            header: 'DURUM',
            accessorKey: 'is_completed',
            size: 140,
            cellClassName: 'text-center',
            cell: ({ getValue }) => {
                const isCompleted = getValue();
                return (
                    <div className="flex justify-center">
                        {isCompleted ? (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded bg-emerald-50 dark:bg-emerald-500/10 text-[10px] font-bold text-emerald-600 dark:text-emerald-500 border border-emerald-100 dark:border-emerald-500/20">
                                <CheckCircleIcon className="w-3 h-3" />
                                TAMAMLANDI
                            </span>
                        ) : (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded bg-amber-50 dark:bg-amber-500/10 text-[10px] font-bold text-amber-600 dark:text-amber-500 border border-amber-100 dark:border-amber-500/20">
                                <ClockIcon className="w-3 h-3" />
                                KİSMİ
                            </span>
                        )}
                    </div>
                );
            }
        },
        {
            header: 'KATILIM TARİHİ',
            accessorKey: 'started_at',
            size: 120,
            cellClassName: 'text-center',
            cell: ({ getValue }) => (
                <div className="flex flex-col items-center">
                    <span className="text-xs font-bold text-slate-700 dark:text-slate-300 tracking-tight">
                        {getValue() ? format(new Date(getValue()), 'dd.MM.yyyy', { locale: tr }) : '-'}
                    </span>
                    <span className="text-[10px] text-slate-400 font-medium">
                        {getValue() ? format(new Date(getValue()), 'HH:mm', { locale: tr }) : '-'}
                    </span>
                </div>
            )
        },
        {
            header: 'İŞLEMLER',
            id: 'actions',
            size: 100,
            cell: ({ row }) => (
                <TableActionGroup justify="end">
                    <TableAction
                        icon={EyeIcon}
                        onClick={() => navigate(`/survey/submissions/detail/${row.original.id}`)}
                        color="blue"
                        title="İncele"
                    />
                </TableActionGroup>
            )
        }
    ];

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
            </div>
        );
    }

    if (!survey) return <div>Anket bulunamadı.</div>;

    return (
        <div className="p-8 max-w-7xl mx-auto space-y-8">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-200 dark:border-slate-800 pb-8">
                <div className="space-y-1">
                    <div className="flex items-center gap-2 text-slate-400 text-[10px] font-bold uppercase tracking-wider mb-2">
                        <Link to="/survey/list" className="hover:text-indigo-600 transition-colors">Anket Listesi</Link>
                        <ChevronRightIcon className="w-3 h-3" />
                        <span className="text-slate-600 dark:text-slate-300">Yanıtlar</span>
                    </div>
                    <h1 className="text-3xl font-bold text-slate-900 dark:text-white tracking-tight italic">
                        {survey.title?.tr || Object.values(survey.title || {})[0]}
                    </h1>
                </div>

                <div className="flex items-center gap-3">
                    <Link
                        to={`/s/${slug}`}
                        target="_blank"
                        className="flex items-center gap-2 px-5 py-2.5 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded font-bold text-xs hover:opacity-90 transition-all active:scale-95 shadow-md"
                    >
                        <span>ANKETİ GÖRÜNTÜLE</span>
                        <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
                    </Link>
                </div>
            </div>

            <DataFilter
                config={filterConfig}
                filters={filters}
                onChange={handleFilterChange}
                onClear={handleClearFilters}
            />

            <DataTable
                data={filteredData}
                columns={columns}
                isLoading={loading}
                showSearch={false}
            />
        </div>
    );
};

export default SurveySubmissions;
